import { useEffect, useState } from 'react';

import { api } from '../api/endpoints';
import type { EventComment, EventReview, Medal } from '../api/types';

interface Props {
  eventId: number;
  /** Записан ли зритель — от этого зависит доступ к обсуждению. */
  joined: boolean;
  isPast: boolean;
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function EventDiscussion({ eventId, joined, isPast }: Props) {
  const [comments, setComments] = useState<EventComment[] | null>(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!joined) {
      setComments([]);
      return;
    }
    api
      .comments(eventId)
      .then(setComments)
      .catch(() => setComments([]));
  }, [eventId, joined]);

  async function send() {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      const created = await api.addComment(eventId, body);
      setComments((current) => [...(current ?? []), created]);
      setDraft('');
    } finally {
      setSending(false);
    }
  }

  async function remove(id: number) {
    await api.deleteComment(eventId, id);
    setComments((current) => (current ?? []).filter((c) => c.id !== id));
  }

  if (!joined) {
    return (
      <section className="block">
        <h2 className="block__title">Обсуждение</h2>
        <p className="muted">
          Открывается после записи. Там договариваются, кто во сколько
          подъедет и кто кого забирает.
        </p>
      </section>
    );
  }

  return (
    <>
      <section className="block">
        <h2 className="block__title">Обсуждение</h2>
        {comments === null && <div className="skeleton" />}
        {comments?.length === 0 && (
          <p className="muted">
            Пока тихо. {isPast ? '' : 'Напишите первым — остальным придёт уведомление.'}
          </p>
        )}

        {comments?.map((comment) => (
          <div key={comment.id} className="msg">
            <div className="msg__head">
              <span className="msg__author">{comment.author}</span>
              <span className="muted small">{when(comment.created_at)}</span>
            </div>
            <div className="msg__body">{comment.body}</div>
            {comment.mine && (
              <button className="msg__drop" onClick={() => remove(comment.id)}>
                удалить
              </button>
            )}
          </div>
        ))}

        <div className="composer">
          <textarea
            className="field__input field__input--area"
            rows={2}
            placeholder="Написать участникам…"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <button className="btn" onClick={send} disabled={sending || !draft.trim()}>
            {sending ? 'Отправляю…' : 'Отправить'}
          </button>
        </div>
      </section>

      {isPast && <ReviewBlock eventId={eventId} />}
    </>
  );
}

const STARS = [1, 2, 3, 4, 5];

function ReviewBlock({ eventId }: { eventId: number }) {
  const [reviews, setReviews] = useState<EventReview[] | null>(null);
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [earned, setEarned] = useState<Medal[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .reviews(eventId)
      .then(setReviews)
      .catch(() => setReviews([]));
  }, [eventId]);

  const mine = reviews?.find((review) => review.mine);

  async function submit() {
    setSaving(true);
    try {
      const medals = await api.addReview(eventId, {
        rating,
        body: body.trim() || null,
        is_public: isPublic,
      });
      setEarned(medals);
      setReviews(await api.reviews(eventId));
      setBody('');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="block">
      <h2 className="block__title">Отзывы</h2>

      {earned.length > 0 && (
        <div className="prize">
          {earned.map((medal) => (
            <div key={medal.code}>
              <span className="prize__icon">{medal.icon}</span> Медаль «{medal.title}»
            </div>
          ))}
        </div>
      )}

      {!mine && (
        <div className="reviewform">
          <p className="muted">
            По отзывам сообщество решает, что делать дальше. За первый отзыв —
            медаль «Голос».
          </p>
          <div className="stars">
            {STARS.map((value) => (
              <button
                key={value}
                type="button"
                className={`star${value <= rating ? ' star--on' : ''}`}
                onClick={() => setRating(value)}
                aria-label={`Оценка ${value}`}
              >
                ★
              </button>
            ))}
          </div>
          <textarea
            className="field__input field__input--area"
            rows={3}
            placeholder="Что понравилось, что стоит поправить"
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
          <label className="checkline">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(event) => setIsPublic(event.target.checked)}
            />
            <span>
              Показывать участникам. Снимите — отзыв увидят только организаторы.
            </span>
          </label>
          <button className="btn" onClick={submit} disabled={saving || rating === 0}>
            {saving ? 'Отправляю…' : 'Оставить отзыв'}
          </button>
        </div>
      )}

      {reviews?.map((review) => (
        <div key={review.id} className="msg">
          <div className="msg__head">
            <span className="msg__author">{review.author}</span>
            <span className="stars stars--small">{'★'.repeat(review.rating)}</span>
          </div>
          {review.body && <div className="msg__body">{review.body}</div>}
        </div>
      ))}

      {reviews?.length === 0 && <p className="muted">Отзывов пока нет.</p>}
    </section>
  );
}
